import useInput from 'lib/hooks/useInput';
import { cutUrl } from 'lib/library';
import { projectEventType } from 'lib/mapper';
import { BASE_SOCKET_PROJECT_URL } from 'lib/setting';
import { useDidUpdateEffect, useShallowSelector } from 'lib/utils';
import _ from 'lodash';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import store from 'store';
import { AppActions, ProjectActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { debounce } from 'lodash';

export const ProjectSocketContext = createContext<any>(null);

// ProjectSocketPage, ProjectUpdate, Chat 관련 socket Provider
export function ProjectSocketProvider({ children }: { children: React.ReactNode }) {
  const { user, projectInfo, accessToken, fetchProjectSuccess } = useShallowAppSelector(state => ({
    user: state.user.user,
    projectInfo: state.project.project.data?.projectInfo,
    accessToken: state.auth.accessToken,
    fetchProjectSuccess: state.project.project.success,
  }));
  const isChatOpen = useInput(false);
  const { pathname, search } = useLocation();
  const history = useHistory();
  const isDetailPage = `${cutUrl(pathname)}/${cutUrl(pathname, 1)}` === 'project/detail';
  const userSyncUid = user?.syncUid;
  const userGroupCode = user?.groupCode;
  const company = user?.company; // === name
  const name = user?.name;
  const senderUid = projectInfo?.ownerSyncUid;
  const userType = projectInfo?.userType;
  const projectCode = projectInfo?.projectCode;
  const receiverCode = projectInfo?.receiverCode;
  const senderCode = projectInfo?.senderCode;
  const stage = projectInfo?.stage;

  const isPossibleJoin =
    userGroupCode === projectInfo?.ownerGroupCode ||
    userGroupCode === projectInfo?.partnerGroupCode ||
    false;

  const projectStatus = projectInfo?.projectStatus;
  // socket state
  // const [socketState, setSocketState] = useState(null);
  const socketState = useRef<any>(null);
  const isRefresh = useInput(false);

  // 초기값 null -> true 변경(23/02/28), connect: true, disconnect: false, socket.open() 용도
  const [isConnect, setIsConnect] = useState<any>(true);
  // cancelApply 확인 값
  const isCancelApply = useInput(null);
  const [chatResponseStatus, setChatResponseStatus] = useState(null);
  const [projectEvent, setProjectEvent] = useState({
    eventType: null,
    result: null,
  });
  const [chatData, setChatData] = useState<any>([]);
  const [newChatData, setNewChatData] = useState<any>();
  const newProjectChat = useInput(0);
  const [chatListData, setChatListData] = useState<any>({});
  const chatListRequestPossible = useInput(true);
  const [chatListAllItems, setChatListAllItems] = useState([]);
  const [isFirstFetchChatList, setIsFirstFetchChatList] = useState(true);
  const [lastUnreadMessageId, setLastUnreadMessageId] = useState<string | null>(null);
  // projectJoin 시 참가자 userSyncUid, company담은 배열
  const [participant, setParticipant] = useState<any>(null);
  const [participantCount, setParticipantCount] = useState(null);
  // projectEvent에 따른 response data
  const [chatUserData, setChatUserData] = useState<any>(null);
  const [updateProjectData, setUpdateProjectData] = useState(null);
  const [applyDesignerData, setApplyDesignerData] = useState(null);
  const [reconnectFlag, setReconnectFlag] = useState(false);
  const [socketId, setSocketId] = useState('');
  const chatDataImgPrev = useInput([]);
  const chatRemoveFileId = useInput(null);
  const [chatDataImgRes, setChatDataImgRes] = useState<any | null>({});
  const chatImageFileListCt = useInput([]);
  const [deleteImageChatId, setDeleteImageChatId] = useState<string>('');
  // project/detail 페이지 에서 socket 연결, 최초 한번 실행
  useEffect(() => {
    // let socket:Socket<DefaultEventsMap, DefaultEventsMap> = null;
    // detail 페이지 입장 시 socket자동 연결
    if (isDetailPage && projectCode) {
      console.log('project socket open');
      let socket = io(BASE_SOCKET_PROJECT_URL, {
        path: '/notification/socket.io',
        autoConnect: false,
        reconnectionDelayMax: 10000,
        auth: { 'x-access-token': store.getState().auth.accessToken },
      });
      socket.open();
      socketState.current = socket;

      socket.on('connect', () => {
        console.log('socket connect_', socket.id, socket.connected);
        !!socket.id && setSocketId(socket.id);
        // socket.open();
        setIsConnect(socket.connected);
        if (isPossibleJoin) handleJoinProject();
      });
      socket.on('disconnect', () => {
        console.log('project socket connect_dis', socket.connected);
        // console.log('socket.id', socket.id);
        // socket.open();
        setIsConnect(socket.connected);
        // setIsReconnect(false);
        if (isPossibleJoin) handleJoinProject();
        // handleJoinProject();
      });
      socket.on('connect_error', (error: any) => {
        console.log('project socket connect_error!', error);
        // setIsCloseSocket(true);
        socket.close();
      });

      // socket projectEvent에 따른 분기
      // 각 event별 state별도로 분리, designer만 통합
      /**
       *  designerEnter: "DESIGNER_ENTER",
       *  projectUpdate: "PROJECT_UPDATE",
       *  designerLeave: "DESIGNER_LEAVE",
       *  applyDesigner: "APPLY_DESIGNER",
       */
      let last;
      socket.on('projectEvent', (data: any) => {
        console.log(socket.id, ' projectEvent ', data);
        const { eventTypeIdx, eventType, syncUid: chatSyncUid, result = null } = data;
        console.log('eventType', eventType);
        if (result === 500) setChatResponseStatus(result);
        if (eventType === projectEventType.userEnter) {
          console.log('enter user');
          setParticipantCount(result?.participantCount || 1);
          setParticipant(result?.participant);
          setChatUserData((draft: any) => ({ ...draft, ...data }));
          const company =
            result?.participant &&
            _.find(result?.participant, item => item.syncUid === chatSyncUid)?.name;
          console.log('chatSyncUid', chatSyncUid);
          console.log('company', company);
          // newUser표시
          setChatData((draft: any) =>
            // draft.concat({
            //   sender: chatSyncUid,
            //   eventType: 'enter',
            //   company,
            // }),
            [
              {
                sender: chatSyncUid,
                eventType: 'enter',
                company,
              },
              ...draft,
            ],
          );
        }

        if (eventType === projectEventType.userLeave) {
          console.log('leave user');
          setParticipantCount(data?.result?.participantCount);
          // setChatUserData(draft => ({ ...draft, ...data }));
          // setChatUserData(draft => ({ ...draft, eventType, syncUid: chatSyncUid, result }));
          // setLeaveDesignerData({ eventType, syncUid: chatSyncUid, result });
          setChatData((draft: any) =>
            // draft.concat({
            //   sender: chatSyncUid,
            //   eventType: 'leave',
            //   company: '',
            // }),
            [
              {
                sender: chatSyncUid,
                eventType: 'leave',
                company: '',
              },
              ...draft,
            ],
          );
        }
        if (eventType === projectEventType.projectUpdate) {
          // apply - stage 0상태
          // accept - stage 0 -> 1
          // working - stage 1 -> 2
          // reject - stage 1 -> clientInfo: null -> client에게 검은 화면 및 프로젝트 상태 알림
          // done - stage 2 -> 3
          // giveup - stage 2 -> clientInfo: null -> client에게 검은 화면 및 프로젝트 상태 알림
          // confirm - stage 3 -> 4
          // rework - stage 3 -> 2
          // changeDesigner - stage 3 -> new -> designer는 result 403 체크(알림 후 history.goBack())
          // cancel - stage 3 -> delete project -> designer는 stage 3, resultStatus 404체크(알림 추가)
          // evaluate 는 Success로 설정
          // NOTE: attendDesigner flow
          // 1: applying designer list(attendDesigners), 2: selected designer list, 3: matched designer list(attendDesigners)
          // 1 ->  (1 - 2) -> 3
          console.log('update project data', data);
          const projectCodeValue = data?.projectCode || projectCode;
          console.log('projectCodeValue', projectCodeValue);
          // setUpdateProjectData({ eventType, syncUid: chatSyncUid, result });
          setUpdateProjectData({ ...data });
          if (projectCodeValue) {
            ProjectActions.fetch_project_request(projectCodeValue);
            // ProjectActions.fetch_projects_request({
            //   page: 1,
            //   keyword: '',
            //   stages: '',
            //   startDate: '',
            //   endDate: '',
            // });
            ProjectActions.fetch_project_histories_request({
              id: projectCodeValue,
              data: { page: 1 },
            });
          }
        }
        if (eventType === projectEventType.applyDesigner) {
          setApplyDesignerData({ ...data });
          console.log('apply designer');
          // request api get 참석 디자이너
        }
      });
      console.log('socket ', socket);

      // 채팅입력 후 response
      socket.on('chatResponse', (data: any) => {
        console.log(socket.id, 'chatResponse payload', data);

        setNewChatData(data);
        if (!!data.images) chatImageResponse(data);
        setChatData((draft: any) => [
          {
            ...data,
            eventType: 'message',
            company: '',
          },
          ...draft,
        ]);
      });

      // 이전 채팅 리스트 불러오기
      socket.on('chatList', (data: any) => {
        console.log('chatList ', data);
        chatListRequestPossible.setValue(true);
        // const {
        //   docs,
        //   hasNextPage,
        //   hasPrevPage,
        //   limit,
        //   nextPage,
        //   page,
        //   pagingCounter,
        //   prevPage,
        //   totalDocs,
        //   totalPages,
        // } = data;
        setChatListData(data);

        const docs = data?.docs;
        // const lastId = data?.page === 1 && docs[docs?.length - 1]?._id;
        // const lastId =
        //   data?.page === 1 && docs?.length > data?.defaultChatCount && docs[docs?.length - 1]._id;
        // if (lastId) setLastUnreadMessageId(lastId);

        const chatListDataItems =
          docs?.reduce((acc: any, curr: any) => {
            curr = {
              ...curr,
              eventType: 'message',
              company: '',
            };

            return acc.concat(curr);
          }, []) || [];
        // .reverse() || [];

        console.log('chatListDataItems', chatListDataItems);
        // setChatListAllItems(draft => [...draft, ...chatListDataItems]);
        setChatData((draft: any) => [...draft, ...chatListDataItems]);
      });

      socket.on('chatImageDelete', (data: any) => {
        const { eventType, chatId } = data;
        if (eventType === projectEventType.chatImageDelete) {
          setDeleteImageChatId(chatId);
        }
      });

      // 채팅입력 후 response
      socket.on('chatImagesUploadResult', (data: any) => {
        chatImageResponse(data);
        setChatDataImgRes({ ...data });
      });

      socket.on('event', (data: any) => {
        console.log('event', data);
      });
    } else {
    }
  }, [isDetailPage, projectCode, reconnectFlag, isPossibleJoin]);

  useEffect(() => {
    if (!newChatData) return;
    if (!isChatOpen.value) {
      if (newChatData?.senderName !== user.name) {
        newProjectChat.setValue(newProjectChat.value + 1);
      }
    } else {
      newProjectChat.setValue(0);
      setNewChatData(null);
    }
  }, [isChatOpen.value, newChatData]);

  const chatImageResponse = debounce(data => {
    // 채팅 Response에 이미지 있는경우, 프로젝트 refetch
    ProjectActions.fetch_project_request(projectCode);
  }, 500);

  useEffect(() => {
    if (chatDataImgPrev.value.length > 0) {
      chatDataImgPrev.value.map((item: any) => {
        if (!!item._id) {
          // 재전송인경우, 채팅 정렬 변경 (재전송인 경우가 맨 앞으로)
          let successFileList: any[] = [];
          let reTryFileList: any[] = [];
          chatData.map((data: any) => {
            if (item?._id === data._id) {
              reTryFileList.push({ ...data, reTry: true });
            } else {
              successFileList.push(data);
            }
          });
          setChatData([...reTryFileList, ...successFileList]);
        } else {
          setChatData((draft: any) => [
            {
              ...item,
              eventType: 'message',
              company: '',
            },
            ...draft,
          ]);
        }
      });
    }
  }, [chatDataImgPrev.value]);

  useEffect(() => {
    if (chatData.length === 0 || !chatDataImgRes.images) return;
    // 이미지업로드 chatImagesUploadResult(채널) 응답 시, 임시로 등록된 채팅(파일전송)정보와 교체
    const chatDataTemp: any[] = chatData.map((item: any) => {
      if (
        !!item?.images &&
        item.images[0].status !== 'success' &&
        item.images[0].status !== 'deleted' &&
        item.images[0].status !== 'delete' &&
        item.images[0].originalname === chatDataImgRes?.images[0]?.originalname
      ) {
        item.images = chatDataImgRes.images;
        item._id = chatDataImgRes._id;
        item.sendDate = chatDataImgRes.sendDate;
        item.message = chatDataImgRes.message;
        item.msgGroupId = chatDataImgRes.msgGroupId;
        item.temp.imageId = chatDataImgRes?.images[0]?.imageId;
      }
      return item;
    });
    setChatData([...chatDataTemp]);
  }, [chatDataImgRes]);

  useEffect(() => {
    if (chatData.length === 0 || !deleteImageChatId) return;
    // 채팅 이미지 삭제 시, 상태값 변경
    const chatDataTemp: any[] = chatData.map((item: any) => {
      if (item._id === deleteImageChatId) {
        item.images[0].status = 'deleted';
      }
      return item;
    });
    setChatData([...chatDataTemp]);
  }, [deleteImageChatId]);

  useDidUpdateEffect(() => {
    // 채팅 전송 실패 파일 삭제 시, 리스트에서 해당 파일 제거
    const removedChatFile =
      chatData?.filter((item: any) => {
        return item._id !== chatRemoveFileId.value;
      }) || [];
    setChatData([...removedChatFile]);
  }, [chatRemoveFileId.value]);

  // 첫 채팅 로드시 작동, 여기까지 읽었습니다 추가
  useDidUpdateEffect(() => {
    if (
      !participant ||
      !userSyncUid ||
      !Array.isArray(chatListData.docs) ||
      lastUnreadMessageId !== null
    ) {
      return;
    }

    const lastReadTime = participant[userSyncUid].lastRead;
    const docs = [...chatListData.docs].reverse();
    const unReadItem = docs?.find((item: any) => {
      return item.sendDate > lastReadTime;
    });
    const lastId = unReadItem ? unReadItem?._id : '';

    if (lastId) setLastUnreadMessageId(lastId);
  }, [participant, userSyncUid, chatListData, lastUnreadMessageId]);

  // 처음 랜더링시의 chatList값만 사용
  // useEffect(() => {
  //   if (isFirstFetchChatList === true) {
  //     console.log('chatListData', chatListData);
  //     // setIsFirstFetchChatList(draft => {
  //     //   if (draft) lastId = docs[docs?.length - 1]._id;
  //     //   return draft;
  //     // });
  //     // // const lastId = docs?.length > 10 && docs[docs?.length - 1]._id;
  //     // let lastId = null;
  //     // setLastUnreadMessageId(lastId);
  //     // setIsFirstFetchChatList(false);
  //   }
  // }, [isFirstFetchChatList, chatListData]);

  // Project Detail을 벗어나는 시점
  // 시점 중요, chatLeave이후 close가 실행되도록 시점을 잡아야함.
  // 자동 lave chat, socket close
  useEffect(() => {
    // socket이 있을 경우는 detailPage에 projectCode를 가지고 있었을 경우, 나올 경우에 가지고 있음
    if (!!socketState.current) {
      if (!isDetailPage) {
        console.log('project socket close, chatLeave');
        socketState?.current.emit('chatLeave', {
          syncUid: userSyncUid,
          projectCode,
        });
        socketState.current.close();
        setLastUnreadMessageId(null);
      }
    }
    // 초기화
    setNewChatData(null);
    setChatData([]);
    setChatUserData(null);
    setChatListData({});
    setChatListAllItems([]);
  }, [isDetailPage, !!socketState.current]);

  // TEST:
  useEffect(() => {
    console.log('chatData', chatData);
  }, [chatData]);

  // disconnect이후 재연결
  // useDidUpdateEffect(() => {
  //   if (isDetailPage && !isConnect) return socket.open();
  // }, [isDetailPage, isConnect]);

  // 채팅방 아웃
  const handleLeaveChat = useCallback(
    (data = {}) => {
      // const { isClose = false } = data;
      if (!userSyncUid || !projectCode) {
        return AppActions.show_toast({ type: 'error', message: 'Bad Request 400' });
      }

      setChatData([]);
      setNewChatData(null);
      console.log('socketState', socketState);
      console.log('before chatLeave socketState', socketState);
      if (!!socketState.current) {
        console.log('socket emit chatLeave', socketState.current.id);
        socketState.current.emit('chatLeave', {
          syncUid: userSyncUid,
          projectCode,
        });
        if (!isDetailPage) {
          socketState.current.close();
        }
        // if (isClose) {
        //   console.log('project socket close');
        //   socketState.current.close();
        // }
      }
      // console.log('setChatData work');
    },
    [socketState.current, userSyncUid, projectCode, isDetailPage],
  );

  const handleJoinProject = useCallback(
    () => {
      console.log('handleJoinProject');
      // NOTE: project 들어올경우 권한없을 시 403이 발생하기때문에 valid check 불필요
      // const { isApply = false } = data;
      // // const { isApply = false, userSyncUid, projectCode, senderCode, company } = data;
      // // const isValidJoinDesigner = !!attendDesignerList?.find(
      // //   item => item.designerCode === userSyncUid,
      // // );
      // // apply일 경우 기존 attendList 확인 안함
      // // apply아닐경우 designer 경우 apply 한 유저인지 확인 (그냥 입장시 채팅방 연결 유무 확인)
      // let isValidJoinDesigner = false;
      // if (isApply) {
      //   isValidJoinDesigner = true;
      // } else {
      //   isValidJoinDesigner =
      //     !!attendDesignerList?.find(item => item.designerCode === userSyncUid) ||
      //     receiverCode === userSyncUid;
      // }
      // // client인지 확인
      // const isValidJoinClient = senderCode === userSyncUid;
      // let validSubmitList = [isValidJoinClient, isValidJoinDesigner];
      // console.log('validSubmitList', validSubmitList);
      // const isValidSubmitValue = validSubmitList.some(item => !!item);
      // if (!isValidSubmitValue) return console.log('isValidSubmitValue', isValidSubmitValue);

      // projectCode
      // syncUid
      // senderUid
      // name
      const joinData = {
        projectCode,
        syncUid: userSyncUid,
        userGroupCode,
        senderUid,
        // name: company,
        name,
        userType,
        isRefresh: isRefresh.value,
      };
      if (!!socketState.current) {
        console.log('socket emit projectJoin', joinData);
        socketState.current.emit('projectJoin', joinData);
      }
      isRefresh.setValue(false);
    },
    [socketState.current, projectInfo, user, isRefresh.value],
    // [socketState, projectInfo, user, isRefresh.value, attendDesignerList],
  );

  const handleTestDisconncet = () => {
    // setIsReconnect(true);
    socketState.current.close();
  };

  useEffect(() => {
    if (!!socketState.current) {
      if (!!socketState.current?.disconnected) {
        setReconnectFlag(!reconnectFlag);
      }
    }
  }, [socketState.current?.disconnected]);

  const handleClickConnect = () => {
    // isRefresh.setValue(true);
    setReconnectFlag(!reconnectFlag);
  };

  return (
    <ProjectSocketContext.Provider
      value={{
        projectCode,
        projectStatus,
        isConnect,
        socketState,
        isCancelApply,
        isRefresh,
        chatUserData,
        socketId,
        chatDataImgPrev,
        chatRemoveFileId,
        chatImageFileListCt,
        // enterDesignerData,
        // leaveDesignerData,
        updateProjectData,
        applyDesignerData,
        chatResponseStatus,
        chatListData,
        chatListRequestPossible,
        lastUnreadMessageId,
        chatListAllItems,
        chatData,
        participant,
        participantCount,
        handleLeaveChat,
        handleJoinProject,
        handleTestDisconncet,
        handleClickConnect,
        isChatOpen,
        newProjectChat,
      }}
    >
      {children}
    </ProjectSocketContext.Provider>
  );
}

// [projectJoin]
// {
//   projectCode: String, // 프로젝트 코드
//   syncUid: String, // 현재 접속한 유저 syncUid
//   name: String, // 유저명(nicnName)
//   senderUid: String // 프로젝트 생성 유저 SyncUid
// }

// [chatSend]
// {
//   chatRoomId: String, // chatRoomId
//   message: String, // 메세지
//   syncUid: String, // 메세지 보낸 유저 SyncUid
// }

// [chatList]
// {
//   chatRoomId: String, // chatRoomId
//   page: Int, // 페이지
//   skip: Int, // 스킵
// }
